import {
    Box,
    Button,
    HStack,
    ModalBody,
    ModalFooter,
    VStack,
} from "@chakra-ui/react";
import { Form } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import Currency from "components/Currency";
import CustomerSelector from "components/CustomerSelector";
import { Roles, useIdentity } from "context/auth";
import { usePendingFilteredPayouts } from "hooks/usePayoutRequests";
import useProfile from "hooks/useProfile";
import { ApplicationUser } from "models/applicationUsers";
import {
    CreatePayoutRequestPayload,
    ValidPaymentMethods
} from "models/payoutRequests";
import { FC, Suspense, useEffect, useState } from "react";
import { dollarsToCents } from "util/helpers";
import AvailableBalance from "../AvailableBalance";
import PayoutMethodDetailsForms from "../PayoutMethodDetailForms";
import AmountFormItem from "../PayoutMethodDetailForms/Inputs/AmountFormItem";
import FormInput from "../PayoutMethodDetailForms/Inputs/FormInput";
import PayoutMethodSelector from "../PayoutMethodSelector";
import useCustomerConsignmentBalance from "../useCustomerConsignmentBalance";

const PayoutDetailsStep1: FC<{
    form: FormInstance<any>;
    accountId: string;
    active: boolean;
    onClose: () => void;
    onNextStep: (payoutRequest: CreatePayoutRequestPayload) => void;
}> = ({ form, accountId, active, onClose, onNextStep }) => {
    const [method, setMethod] = useState<ValidPaymentMethods | undefined>();
    const { data: profile } = useProfile();
    const customer = Form.useWatch('customer', form);
    const { isInRole } = useIdentity();
    const isAdmin = isInRole([Roles.Admin, Roles.SeniorStaff]);
    const { getBalance } = useCustomerConsignmentBalance();
    const [customerBalanceInCents, setCustomerBalanceInCents] = useState<number>();

    useEffect(() => {
        // setting form defaults based on profile data whenever
        // the payout method changes
        if (profile) {
            const fullName = `${profile?.firstName} ${profile?.lastName}`;

            form.setFieldsValue({
                name: fullName,
                payee: fullName,
                nameOnAccount: fullName,
                ...profile
            });
        }
    }, [form, method, profile]);

    const onFinish = async (values: any) => {
        // TODO: When the user tries to hit next without selecting a method, show an error message
        if (!method) {
            return;
        }

        const { amount, customer, ...rest } = values;

        const customerObj = customer as ApplicationUser | undefined;

        const payoutRequest: CreatePayoutRequestPayload = {
            method,
            amountInCents: dollarsToCents(amount),
            customerId: customerObj?.id,
            // don't send the account id if an admin is acting on behalf of a customer,
            // the backend will resolve it to the customer's consignment account
            accountId: isAdmin ? null : accountId,
            details: {
                // Everything else in the form is the dynamic part of the details
                ...rest,
            },
        };

        onNextStep(payoutRequest);
    };

    const resetForm = () => {
        form.resetFields();
        setCustomerBalanceInCents(undefined);
        setMethod(undefined);
    }

    // Find out if the user already has the selected method in a pending request
    // If this is an admin, then don't check
    const { data: pendingPayouts } = usePendingFilteredPayouts();
    const selectedMethodExists =
        !isAdmin &&
        method &&
        pendingPayouts?.data.some((payout) => payout.method === method);
    const submitDisabled = selectedMethodExists;

    return active ? (
        <Box as={Form} form={form} layout="vertical" onFinish={onFinish}>
            <ModalBody>
                <VStack
                    spacing={2}
                    w="100%"
                    align="start"
                    maxW={{ base: "100%", md: "40rem" }}
                >
                    {isAdmin ? (
                        <HStack spacing={2} w="100%" align="start">
                            <FormInput
                                label="Customer"
                                name="customer"
                                minW="15rem"
                            >
                                <CustomerSelector
                                    onSelected={(customer) => {
                                        resetForm();
                                        form.setFieldsValue({ customer });
                                        if (customer) {
                                            getBalance(customer.id).then(result => setCustomerBalanceInCents(result.balanceInCents));
                                        }
                                    }}
                                    onClear={resetForm}
                                    initialEntity={form.getFieldValue("customer")}
                                />
                            </FormInput>
                            {customerBalanceInCents && <FormInput label="Current Balance">
                                <Currency cents={customerBalanceInCents!} />
                            </FormInput>}
                        </HStack>
                    ) : (
                        <Box fontSize={26} width="13.5em">
                            <Suspense>
                                <AvailableBalance accountId={accountId} />
                            </Suspense>
                        </Box>
                    )}

                    <HStack spacing={2} w="100%" align="start">
                        <PayoutMethodSelector
                            value={method}
                            onSelect={setMethod}
                            selectedMethodExists={selectedMethodExists}
                        />
                        <AmountFormItem />
                    </HStack>
                    <PayoutMethodDetailsForms method={method} customer={customer} />
                </VStack>
            </ModalBody>
            <ModalFooter as={HStack}>
                <Button variant="danger" onClick={onClose} size="sm">
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={submitDisabled}
                >
                    Review Request
                </Button>
            </ModalFooter>
        </Box>
    ) : null;
};

export default PayoutDetailsStep1;
